<template>
  <div class="sector-analysis-page">
    <div class="analysis-shell">
      <section class="analysis-panel hero-panel">
        <div class="hero-copy">
          <p class="eyebrow">Sector Analysis</p>
          <h2>Portfolio strength by sector, with stock-level risk signals.</h2>
          <p class="hero-description">
            Click a sector card to open the deep dive. The dashboard uses your current portfolio
            stocks and sector master data.
          </p>
        </div>
        <div class="hero-actions">
          <div class="search-box">
            <i class="fa fa-search"></i>
            <input v-model.trim="searchTerm" type="text" placeholder="Search sector by name" />
          </div>
          <button
            type="button"
            class="export-button"
            :disabled="!normalizedSectors.length || isLoadingSectors"
            @click="exportSectorAnalysis"
          >
            <i class="fa fa-download"></i>
            Export sector report
          </button>
          <div class="hero-metrics">
            <div>
              <span>Total Invested</span>
              <strong>{{ formatCurrency(totalInvested) }}</strong>
            </div>
            <div>
              <span>Current Value</span>
              <strong>{{ formatCurrency(totalCurrentValue) }}</strong>
            </div>
            <div>
              <span>Portfolio P&amp;L</span>
              <strong :class="getTextClass(totalPlPercent)">{{ formatSignedCurrency(totalPlAmount) }}</strong>
            </div>
          </div>
        </div>
      </section>

      <div class="sector-workspace">
        <section class="analysis-panel sector-list-panel">
          <div class="panel-header">
            <div>
              <p class="section-kicker">Section 1</p>
              <h3>Sector Summary Cards</h3>
            </div>
            <span class="result-count">{{ filteredSectors.length }} sectors</span>
          </div>

          <div v-if="isLoadingSectors" class="empty-state">
            Loading sector summary...
          </div>

          <div v-else-if="loadError" class="empty-state error-state">
            {{ loadError }}
          </div>

          <div v-else-if="!filteredSectors.length" class="empty-state">
            No sectors matched "<strong>{{ searchTerm }}</strong>".
          </div>

          <div v-else class="sector-grid">
            <button
              v-for="sector in filteredSectors"
              :key="sector.sectorName"
              type="button"
              class="sector-card"
              :class="[{ active: selectedSector?.sectorName === sector.sectorName }, getCardClass(sector)]"
              @click="selectSector(sector)"
            >
              <div class="sector-card-header">
                <div>
                  <h4>{{ sector.sectorName }}</h4>
                  <p>{{ formatCurrency(sector.totalInvested) }} invested</p>
                </div>
                <span class="stock-count">{{ sector.stocks.length }} stocks</span>
              </div>

              <div class="sector-values">
                <div>
                  <span>Current Value</span>
                  <strong>{{ formatCurrency(sector.currentValue) }}</strong>
                </div>
                <div>
                  <span>P&amp;L</span>
                  <strong :class="getTextClass(sector.plPercent)">
                    {{ getArrow(sector.plPercent) }} {{ formatSignedCurrency(sector.plAmount) }}
                  </strong>
                  <small :class="getTextClass(sector.plPercent)">{{ formatPercent(sector.plPercent) }}</small>
                </div>
              </div>

              <div class="weight-row">
                <div class="weight-meta">
                  <span>Portfolio Weight</span>
                  <strong>{{ formatPercent(sector.portfolioWeight) }}</strong>
                </div>
                <div class="weight-bar">
                  <div class="weight-fill" :style="{ width: `${Math.min(sector.portfolioWeight, 100)}%` }"></div>
                </div>
              </div>
            </button>
          </div>
        </section>

        <section v-if="selectedSector" class="analysis-panel deep-dive-panel">
          <div class="panel-header">
            <div>
              <p class="section-kicker">Section 2</p>
              <h3>{{ selectedSector.sectorName }} Deep Dive</h3>
            </div>
            <button type="button" class="route-link" @click="openSectorStocks(selectedSector)">Open matching stocks</button>
          </div>

          <div class="deep-dive-summary">
            <div class="summary-pill">
              <span>Stocks</span>
              <strong>{{ selectedSector.stocks.length }}</strong>
            </div>
            <div class="summary-pill">
              <span>Best P&amp;L</span>
              <strong class="text-success">{{ formatPercent(selectedSectorTopPerformer.plPercent) }}</strong>
            </div>
            <div class="summary-pill">
              <span>Avg 52W Position</span>
              <strong>{{ formatPercent(selectedSectorRisk.avg52WPosition) }}</strong>
            </div>
            <div class="summary-pill">
              <span>Penny Stocks</span>
              <strong>{{ selectedSectorRisk.pennyStockCount }}</strong>
            </div>
          </div>

          <div class="table-shell">
            <table class="table sector-stock-table">
              <thead>
                <tr>
                  <th
                    v-for="header in stockHeaders"
                    :key="header.key"
                    :class="{ sortable: header.sortable, active: stockSort.key === header.key }"
                    @click="header.sortable && sortStocks(header.key)"
                  >
                    {{ header.label }}
                    <span v-if="header.sortable && stockSort.key === header.key">
                      {{ stockSort.order === 'desc' ? 'v' : '^' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="stock in sortedSelectedStocks"
                  :key="stock.code"
                  :class="{ 'exit-row': stock.plPercent < -40 }"
                >
                  <td>
                    <div class="stock-name">
                      <strong>{{ stock.name }}</strong>
                      <small>{{ stock.code }}</small>
                    </div>
                  </td>
                  <td>{{ formatCurrency(stock.invested) }}</td>
                  <td>{{ formatCurrency(stock.currentValue) }}</td>
                  <td :class="getTextClass(stock.plPercent)">{{ formatPercent(stock.plPercent) }}</td>
                  <td>{{ formatCurrency(stock.week52Low) }}</td>
                  <td>{{ formatCurrency(stock.week52High) }}</td>
                  <td>
                    <div class="range-stack">
                      <div class="range-bar">
                        <div class="range-fill" :style="{ width: `${get52WPosition(stock)}%` }"></div>
                      </div>
                      <small>{{ formatPercent(get52WPosition(stock)) }}</small>
                    </div>
                  </td>
                  <td>
                    <div class="badge-stack">
                      <span
                        v-for="badge in getRiskBadges(stock)"
                        :key="`${stock.code}-${badge}`"
                        class="risk-badge"
                        :class="getRiskBadgeClass(badge)"
                      >
                        {{ badge }}
                      </span>
                      <span v-if="!getRiskBadges(stock).length" class="risk-badge neutral">Stable</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section class="analysis-panel intelligence-panel">
        <div class="panel-header">
          <div>
            <p class="section-kicker">Section 3</p>
            <h3>Sector Intelligence Panel</h3>
          </div>
        </div>

        <div class="tab-strip">
          <button
            v-for="tab in intelligenceTabs"
            :key="tab.key"
            type="button"
            class="tab-button"
            :class="{ active: activeTab === tab.key }"
            @click="activeTab = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-if="activeTab === 'allocation'" class="tab-panel">
          <div class="chart-card">
            <Doughnut :data="allocationChartData" :options="allocationChartOptions" />
          </div>
          <div class="insight-list">
            <div v-for="sector in normalizedSectors" :key="`allocation-${sector.sectorName}`" class="insight-row">
              <div>
                <strong>{{ sector.sectorName }}</strong>
                <p>{{ formatPercent(sector.portfolioWeight) }} of portfolio</p>
              </div>
              <span v-if="sector.portfolioWeight > 15" class="overweight-badge">Overweight</span>
            </div>
          </div>
        </div>

        <div v-else-if="activeTab === 'performance'" class="tab-panel">
          <div class="chart-card wide">
            <Bar :data="performanceChartData" :options="performanceChartOptions" />
          </div>
        </div>

        <div v-else-if="activeTab === 'risk'" class="tab-panel">
          <div class="heatmap">
            <div class="heatmap-header">Sector</div>
            <div v-for="column in heatmapColumns" :key="`header-${column.key}`" class="heatmap-header">
              {{ column.label }}
            </div>

            <template v-for="row in heatmapRows" :key="row.sectorName">
              <div class="heatmap-sector">{{ row.sectorName }}</div>
              <div
                v-for="column in heatmapColumns"
                :key="`${row.sectorName}-${column.key}`"
                class="heatmap-cell"
                :class="getHeatLevelClass(row[column.key].level)"
              >
                <strong>{{ row[column.key].level }}</strong>
                <small>{{ row[column.key].value }}</small>
              </div>
            </template>
          </div>
        </div>

        <div v-else class="tab-panel suggestions-panel">
          <div v-if="suggestionCards.length" class="suggestion-grid">
            <div v-for="suggestion in suggestionCards" :key="`${suggestion.sector}-${suggestion.title}`" class="suggestion-card">
              <p class="suggestion-sector">{{ suggestion.sector }}</p>
              <h4>{{ suggestion.title }}</h4>
              <p>{{ suggestion.message }}</p>
            </div>
          </div>
          <div v-else class="empty-state">
            No sector suggestions for the current portfolio.
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import * as XLSX from 'xlsx';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { Bar, Doughnut } from 'vue-chartjs';
import { fetchWrapper } from '@/helpers';
import { platformStore, useAlertStore } from '@/stores';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip
} from 'chart.js';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const STOCK_LIST_PLATFORM_WISE = gql`
  query StockListPlatformWise($platformIds: [ID], $advancedFilters: [String]) {
    stockListPlatformWise(platform_ids: $platformIds, advanced_filters: $advancedFilters) {
      stockData {
        stock_id
        name
        code
        investment
        current_value
        market_price
        profit_loss
        profit_loss_per
        sector_id
        fifty_two_week_low
        fifty_two_week_high
      }
    }
  }
`;

type StockRow = {
  code: string;
  name: string;
  invested: number;
  currentValue: number;
  plPercent: number;
  price: number;
  week52Low: number;
  week52High: number;
};

type SectorRow = {
  sectorId?: string;
  sectorName: string;
  portfolioWeight: number;
  totalInvested: number;
  currentValue: number;
  plPercent?: number;
  stocks: StockRow[];
};

export default {
  name: 'sector-summary',
  components: {
    Doughnut,
    Bar
  },
  props: {
    sectorData: {
      type: Array,
      default: () => []
    }
  },
  setup() {
    const platformState = platformStore();
    const { platform } = storeToRefs(platformState);
    return {
      alertStore$: useAlertStore(),
      platformOptions: platform
    };
  },
  data() {
    return {
      searchTerm: '',
      activeTab: 'allocation',
      selectedSectorName: '',
      sectors: [],
      stockListPlatformWise: {
        stockData: []
      },
      isLoadingSectors: false,
      loadError: '',
      stockSort: {
        key: 'plPercent',
        order: 'desc'
      },
      intelligenceTabs: [
        { key: 'allocation', label: 'Allocation' },
        { key: 'performance', label: 'Performance' },
        { key: 'risk', label: 'Risk Heatmap' },
        { key: 'suggestions', label: 'Suggestions' }
      ],
      stockHeaders: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'invested', label: 'Invested', sortable: true },
        { key: 'currentValue', label: 'Current Value', sortable: true },
        { key: 'plPercent', label: 'P&L%', sortable: true },
        { key: 'week52Low', label: '52W Low', sortable: true },
        { key: 'week52High', label: '52W High', sortable: true },
        { key: 'rangePosition', label: '52W Range Position', sortable: true },
        { key: 'risk', label: 'Risk Flags', sortable: false }
      ],
      heatmapColumns: [
        { key: 'concentration', label: 'Concentration' },
        { key: 'avg52WPosition', label: 'Avg 52W Position' },
        { key: 'pennyStockCount', label: 'Penny Stock Count' },
        { key: 'lossDepth', label: 'Loss Depth' }
      ]
    };
  },
  computed: {
    dynamicSectors(): any[] {
      const stockData = Array.isArray(this.stockListPlatformWise?.stockData)
        ? this.stockListPlatformWise.stockData
        : [];
      const sectorMap = new Map<string, any>();

      stockData.forEach((stock: any) => {
        const sectorId = this.getPrimarySectorId(stock.sector_id);
        const sectorName = this.getSectorName(sectorId);
        const key = sectorId || sectorName;
        const invested = Number(stock.investment ?? 0);
        const plAmount = Number(stock.profit_loss ?? 0);
        const currentValue = Number(stock.current_value ?? (invested + plAmount));

        if (!sectorMap.has(key)) {
          sectorMap.set(key, {
            sectorId,
            sectorName,
            portfolioWeight: 0,
            totalInvested: 0,
            currentValue: 0,
            stocks: []
          });
        }

        const sector = sectorMap.get(key);
        sector.totalInvested += invested;
        sector.currentValue += currentValue;
        sector.stocks.push({
          code: stock.code,
          name: stock.name,
          invested,
          currentValue,
          plAmount,
          plPercent: this.normalizePercent(stock.profit_loss_per ?? (invested ? plAmount / invested : 0)),
          price: Number(stock.market_price ?? 0),
          week52Low: Number(stock.fifty_two_week_low ?? 0),
          week52High: Number(stock.fifty_two_week_high ?? 0)
        });
      });

      const portfolioValue = [...sectorMap.values()].reduce((sum, sector) => sum + sector.currentValue, 0);
      return [...sectorMap.values()]
        .map((sector) => {
          const plAmount = sector.currentValue - sector.totalInvested;
          return {
            ...sector,
            plAmount,
            plPercent: sector.totalInvested ? (plAmount / sector.totalInvested) * 100 : 0,
            portfolioWeight: portfolioValue ? (sector.currentValue / portfolioValue) * 100 : 0
          };
        })
        .sort((a, b) => b.currentValue - a.currentValue);
    },
    normalizedSectors(): any[] {
      const input = Array.isArray(this.sectorData) && this.sectorData.length ? this.sectorData : this.dynamicSectors;
      return input.map((sector: SectorRow) => {
        const stocks = (sector.stocks || []).map((stock: any) => {
          const invested = Number(stock.invested ?? stock.investedAmount ?? 0);
          const currentValue = Number(stock.currentValue ?? 0);
          const plAmount = currentValue - invested;
          const plPercent = Number(stock.plPercent ?? (invested ? (plAmount / invested) * 100 : 0));
          return {
            code: stock.code,
            name: stock.name,
            invested,
            currentValue,
            plAmount,
            plPercent,
            price: Number(stock.price ?? 0),
            week52Low: Number(stock.week52Low ?? 0),
            week52High: Number(stock.week52High ?? 0)
          };
        });
        const totalInvested = Number(sector.totalInvested ?? 0);
        const currentValue = Number(sector.currentValue ?? 0);
        const plAmount = currentValue - totalInvested;
        const plPercent = Number(sector.plPercent ?? (totalInvested ? (plAmount / totalInvested) * 100 : 0));
        return {
          sectorId: sector.sectorId,
          sectorName: sector.sectorName,
          portfolioWeight: Number(sector.portfolioWeight ?? 0),
          totalInvested,
          currentValue,
          plAmount,
          plPercent,
          stocks
        };
      });
    },
    filteredSectors(): any[] {
      const term = this.searchTerm.toLowerCase();
      return this.normalizedSectors.filter((sector) => sector.sectorName.toLowerCase().includes(term));
    },
    selectedSector(): any {
      if (!this.filteredSectors.length) {
        return null;
      }
      return this.filteredSectors.find((sector) => sector.sectorName === this.selectedSectorName) || this.filteredSectors[0];
    },
    sortedSelectedStocks(): any[] {
      const stocks = [...(this.selectedSector?.stocks || [])];
      const { key, order } = this.stockSort;
      return stocks.sort((a, b) => {
        const aValue = key === 'rangePosition' ? this.get52WPosition(a) : a[key];
        const bValue = key === 'rangePosition' ? this.get52WPosition(b) : b[key];
        if (typeof aValue === 'string') {
          return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      });
    },
    totalInvested(): number {
      return this.normalizedSectors.reduce((sum, sector) => sum + sector.totalInvested, 0);
    },
    totalCurrentValue(): number {
      return this.normalizedSectors.reduce((sum, sector) => sum + sector.currentValue, 0);
    },
    totalPlAmount(): number {
      return this.totalCurrentValue - this.totalInvested;
    },
    totalPlPercent(): number {
      return this.totalInvested ? (this.totalPlAmount / this.totalInvested) * 100 : 0;
    },
    selectedSectorTopPerformer(): any {
      return [...(this.selectedSector?.stocks || [])].sort((a, b) => b.plPercent - a.plPercent)[0] || { plPercent: 0 };
    },
    selectedSectorRisk(): any {
      return this.getRiskProfile(this.selectedSector);
    },
    allocationChartData(): any {
      return {
        labels: this.normalizedSectors.map((sector) => sector.sectorName),
        datasets: [
          {
            data: this.normalizedSectors.map((sector) => sector.portfolioWeight),
            backgroundColor: ['#155e75', '#1d4ed8', '#16a34a', '#d97706', '#7c3aed', '#dc2626'],
            borderWidth: 0
          }
        ]
      };
    },
    allocationChartOptions(): any {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom'
          },
          tooltip: {
            callbacks: {
              label: (context: any) => `${context.label}: ${this.formatPercent(context.raw)}`
            }
          }
        },
        cutout: '68%'
      };
    },
    performanceChartData(): any {
      const sectors = [...this.normalizedSectors].sort((a, b) => b.plPercent - a.plPercent);
      return {
        labels: sectors.map((sector) => sector.sectorName),
        datasets: [
          {
            data: sectors.map((sector) => sector.plPercent),
            backgroundColor: sectors.map((sector) => (sector.plPercent >= 0 ? '#16a34a' : '#dc2626')),
            borderRadius: 8
          }
        ]
      };
    },
    performanceChartOptions(): any {
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context: any) => this.formatPercent(context.raw)
            }
          }
        },
        scales: {
          x: {
            ticks: {
              callback: (value: number) => `${value}%`
            }
          }
        }
      };
    },
    heatmapRows(): any[] {
      return this.normalizedSectors.map((sector) => {
        const profile = this.getRiskProfile(sector);
        return {
          sectorName: sector.sectorName,
          concentration: this.describeHeat(profile.weight, [
            { max: 10, level: 'Low' },
            { max: 15, level: 'Medium' },
            { max: Infinity, level: 'High' }
          ], this.formatPercent(profile.weight)),
          avg52WPosition: this.describeHeat(profile.avg52WPosition, [
            { max: 30, level: 'High' },
            { max: 65, level: 'Medium' },
            { max: Infinity, level: 'Low' }
          ], this.formatPercent(profile.avg52WPosition)),
          pennyStockCount: this.describeHeat(profile.pennyStockCount, [
            { max: 0, level: 'Low' },
            { max: 1, level: 'Medium' },
            { max: Infinity, level: 'High' }
          ], `${profile.pennyStockCount} names`),
          lossDepth: this.describeHeat(profile.lossDepth, [
            { max: 10, level: 'Low' },
            { max: 30, level: 'Medium' },
            { max: Infinity, level: 'High' }
          ], this.formatPercent(profile.lossDepth))
        };
      });
    },
    suggestionCards(): any[] {
      const suggestions: any[] = [];
      this.normalizedSectors.forEach((sector) => {
        if (sector.plPercent < -30) {
          suggestions.push({
            sector: sector.sectorName,
            title: 'Consider exiting',
            message: 'Prolonged underperformance is pressuring capital efficiency in this sector.'
          });
        }
        if (sector.portfolioWeight > 15) {
          suggestions.push({
            sector: sector.sectorName,
            title: 'Overweight',
            message: 'This sector dominates the portfolio. Consider trimming or rebalancing exposure.'
          });
        }
        if (sector.stocks.some((stock: any) => stock.plPercent > 100)) {
          suggestions.push({
            sector: sector.sectorName,
            title: 'Winning sector',
            message: 'A multi-bagger is present here. Consider adding on dips rather than chasing strength.'
          });
        }
      });
      return suggestions;
    }
  },
  watch: {
    filteredSectors: {
      immediate: true,
      handler(sectors) {
        if (!sectors.length) {
          this.selectedSectorName = '';
          return;
        }
        if (!sectors.some((sector: any) => sector.sectorName === this.selectedSectorName)) {
          this.selectedSectorName = sectors[0].sectorName;
        }
      }
    }
  },
  methods: {
    parsePlatformOptions() {
      const raw = this.platformOptions;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      try {
        return JSON.parse(raw);
      } catch (e) {
        return String(raw).split(',').map((item) => item.trim()).filter(Boolean);
      }
    },
    normalizePercent(value: any) {
      const numberValue = Number(value) || 0;
      return Math.abs(numberValue) <= 1 ? numberValue * 100 : numberValue;
    },
    getPrimarySectorId(value: any) {
      return String(value ?? '').split(',').map((id) => id.trim()).filter(Boolean)[0] || '';
    },
    getSectorName(id: any) {
      if (!id) return 'Unassigned';
      const sector = (this.sectors || []).find((item: any) => String(item.sector_id) === String(id));
      return sector ? sector.name : `Sector ${id}`;
    },
    async fetchSectors() {
      try {
        const response = await fetchWrapper.post('api/sector');
        this.sectors = Array.isArray(response.data) ? response.data : [];
      } catch (error: any) {
        this.loadError = 'Failed to fetch sectors.';
        this.alertStore$.error(this.loadError);
      }
    },
    exportSectorAnalysis() {
      if (!this.normalizedSectors.length) {
        this.alertStore$.error('No sector data available to export.');
        return;
      }

      const workbook = XLSX.utils.book_new();
      const generatedAt = new Date().toLocaleString('en-IN');
      const bestSector = [...this.normalizedSectors].sort((a, b) => b.plPercent - a.plPercent)[0];
      const worstSector = [...this.normalizedSectors].sort((a, b) => a.plPercent - b.plPercent)[0];

      const overviewRows = [
        { Metric: 'Generated At', Value: generatedAt },
        { Metric: 'Total Sectors', Value: this.normalizedSectors.length },
        { Metric: 'Total Stocks', Value: this.normalizedSectors.reduce((sum, sector) => sum + sector.stocks.length, 0) },
        { Metric: 'Total Invested', Value: this.roundForExport(this.totalInvested) },
        { Metric: 'Current Value', Value: this.roundForExport(this.totalCurrentValue) },
        { Metric: 'Portfolio P&L Amount', Value: this.roundForExport(this.totalPlAmount) },
        { Metric: 'Portfolio P&L %', Value: this.roundForExport(this.totalPlPercent) },
        { Metric: 'Best Sector', Value: bestSector ? `${bestSector.sectorName} (${this.formatPercent(bestSector.plPercent)})` : '' },
        { Metric: 'Weakest Sector', Value: worstSector ? `${worstSector.sectorName} (${this.formatPercent(worstSector.plPercent)})` : '' }
      ];

      const sectorRows = this.normalizedSectors.map((sector, index) => {
        const risk = this.getRiskProfile(sector);
        return {
          Rank: index + 1,
          'Sector Name': sector.sectorName,
          'Sector ID': sector.sectorId || '',
          Stocks: sector.stocks.length,
          'Total Invested': this.roundForExport(sector.totalInvested),
          'Current Value': this.roundForExport(sector.currentValue),
          'P&L Amount': this.roundForExport(sector.plAmount),
          'P&L %': this.roundForExport(sector.plPercent),
          'Portfolio Weight %': this.roundForExport(sector.portfolioWeight),
          'Avg 52W Position %': this.roundForExport(risk.avg52WPosition),
          'Penny Stock Count': risk.pennyStockCount,
          'Max Loss Depth %': this.roundForExport(risk.lossDepth),
          'Top Stock': this.getTopStockName(sector),
          'Risk Note': this.getSectorRiskNote(sector)
        };
      });

      const stockRows = this.normalizedSectors.flatMap((sector) => {
        return sector.stocks.map((stock: any) => ({
          'Sector Name': sector.sectorName,
          'Sector ID': sector.sectorId || '',
          'Stock Name': stock.name,
          Code: stock.code,
          Invested: this.roundForExport(stock.invested),
          'Current Value': this.roundForExport(stock.currentValue),
          'P&L Amount': this.roundForExport(stock.plAmount),
          'P&L %': this.roundForExport(stock.plPercent),
          'Market Price': this.roundForExport(stock.price),
          '52W Low': this.roundForExport(stock.week52Low),
          '52W High': this.roundForExport(stock.week52High),
          '52W Position %': this.roundForExport(this.get52WPosition(stock)),
          'Risk Flags': this.getRiskBadges(stock).join(', ') || 'Stable',
          'Action Signal': this.getStockActionSignal(stock)
        }));
      });

      const heatmapRows = this.heatmapRows.map((row) => ({
        Sector: row.sectorName,
        'Concentration Level': row.concentration.level,
        'Concentration Value': row.concentration.value,
        '52W Position Level': row.avg52WPosition.level,
        '52W Position Value': row.avg52WPosition.value,
        'Penny Stock Level': row.pennyStockCount.level,
        'Penny Stock Value': row.pennyStockCount.value,
        'Loss Depth Level': row.lossDepth.level,
        'Loss Depth Value': row.lossDepth.value
      }));

      const suggestionRows = this.suggestionCards.length
        ? this.suggestionCards.map((suggestion) => ({
          Sector: suggestion.sector,
          Title: suggestion.title,
          Message: suggestion.message
        }))
        : [{ Sector: 'Portfolio', Title: 'No suggestions', Message: 'No sector suggestions for the current portfolio.' }];

      this.appendExportSheet(workbook, 'Overview', overviewRows);
      this.appendExportSheet(workbook, 'Sector Summary', sectorRows);
      this.appendExportSheet(workbook, 'Stock Details', stockRows);
      this.appendExportSheet(workbook, 'Risk Heatmap', heatmapRows);
      this.appendExportSheet(workbook, 'Suggestions', suggestionRows);

      XLSX.writeFile(workbook, `SectorAnalysis-${this.getExportDateStamp()}.xlsx`);
    },
    appendExportSheet(workbook: any, sheetName: string, rows: any[]) {
      const sheet = XLSX.utils.json_to_sheet(rows);
      const headers = Object.keys(rows[0] || {});
      sheet['!cols'] = headers.map((header) => ({
        wch: Math.max(header.length + 2, 14)
      }));
      XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
    },
    roundForExport(value: any) {
      const numberValue = Number(value) || 0;
      return Number(numberValue.toFixed(2));
    },
    getExportDateStamp() {
      const now = new Date();
      const pad = (value: number) => String(value).padStart(2, '0');
      return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    },
    getTopStockName(sector: any) {
      const stock = [...(sector?.stocks || [])].sort((a, b) => b.plPercent - a.plPercent)[0];
      return stock ? `${stock.name} (${this.formatPercent(stock.plPercent)})` : '';
    },
    getSectorRiskNote(sector: any) {
      const notes = [];
      if (sector.portfolioWeight > 15) notes.push('Overweight');
      if (sector.plPercent < -30) notes.push('Deep loss');
      if (sector.stocks.some((stock: any) => Number(stock.price) < 10)) notes.push('Penny exposure');
      if (sector.stocks.some((stock: any) => stock.plPercent > 100)) notes.push('Multi-bagger present');
      return notes.join(', ') || 'Stable';
    },
    getStockActionSignal(stock: any) {
      if (stock.plPercent < -40) return 'Review exit';
      if (this.getRiskBadges(stock).includes('Near Low')) return 'Watch near 52W low';
      if (stock.plPercent > 100) return 'Protect gains';
      return 'Hold / monitor';
    },
    formatCurrency(value: number) {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
      }).format(Number(value) || 0);
    },
    formatSignedCurrency(value: number) {
      const amount = this.formatCurrency(Math.abs(value));
      if (value > 0) return `+${amount}`;
      if (value < 0) return `-${amount}`;
      return amount;
    },
    formatPercent(value: number) {
      return `${(Number(value) || 0).toFixed(1)}%`;
    },
    getArrow(value: number) {
      if (value > 0) return '^';
      if (value < 0) return 'v';
      return '-';
    },
    getTextClass(value: number) {
      if (value > 0) return 'text-success';
      if (value < 0) return 'text-error';
      return 'text-muted';
    },
    getCardClass(sector: any) {
      if (sector.plPercent > 0) return 'border-success';
      if (sector.plPercent > -5) return 'border-warning';
      return 'border-danger';
    },
    selectSector(sector: any) {
      this.selectedSectorName = sector.sectorName;
    },
    get52WPosition(stock: any) {
      const low = Number(stock.week52Low) || 0;
      const high = Number(stock.week52High) || 0;
      const price = Number(stock.price) || 0;
      if (high <= low) return 0;
      return Math.max(0, Math.min(100, ((price - low) / (high - low)) * 100));
    },
    getRiskBadges(stock: any) {
      const badges = [];
      const low = Number(stock.week52Low) || 0;
      if (low > 0 && Number(stock.price) <= low * 1.15) {
        badges.push('Near Low');
      }
      if (Number(stock.plPercent) > 100) {
        badges.push('Multi-bagger');
      }
      if (Number(stock.price) < 10) {
        badges.push('Penny Risk');
      }
      return badges;
    },
    getRiskBadgeClass(label: string) {
      if (label === 'Near Low') return 'warning';
      if (label === 'Multi-bagger') return 'success';
      if (label === 'Penny Risk') return 'danger';
      return 'neutral';
    },
    sortStocks(key: string) {
      if (this.stockSort.key === key) {
        this.stockSort.order = this.stockSort.order === 'desc' ? 'asc' : 'desc';
        return;
      }
      this.stockSort.key = key;
      this.stockSort.order = key === 'name' ? 'asc' : 'desc';
    },
    getRiskProfile(sector: any) {
      const stocks = sector?.stocks || [];
      const avg52WPosition = stocks.length
        ? stocks.reduce((sum: number, stock: any) => sum + this.get52WPosition(stock), 0) / stocks.length
        : 0;
      const pennyStockCount = stocks.filter((stock: any) => Number(stock.price) < 10).length;
      const lossDepth = stocks.length
        ? Math.abs(Math.min(...stocks.map((stock: any) => Number(stock.plPercent) || 0)))
        : 0;
      return {
        weight: Number(sector?.portfolioWeight) || 0,
        avg52WPosition,
        pennyStockCount,
        lossDepth
      };
    },
    describeHeat(value: number, ranges: any[], formattedValue: string) {
      const match = ranges.find((range) => value <= range.max) || ranges[ranges.length - 1];
      return {
        level: match.level,
        value: formattedValue
      };
    },
    getHeatLevelClass(level: string) {
      return `heat-${level.toLowerCase()}`;
    },
    openSectorStocks(item: any) {
      this.$router.push({
        path: '/stock/list',
        query: {
          sector_id: String(item?.sectorId || '')
        }
      });
    }
  },
  async created() {
    await this.fetchSectors();
  },
  apollo: {
    stockListPlatformWise: {
      query: STOCK_LIST_PLATFORM_WISE,
      variables() {
        return {
          platformIds: this.parsePlatformOptions(),
          advancedFilters: ['only_portfolio_stocks']
        };
      },
      result({ data }: any) {
        this.stockListPlatformWise = data?.stockListPlatformWise || { stockData: [] };
        this.loadError = '';
      },
      loadingKey: 'isLoadingSectors',
      error(error: any) {
        this.loadError = 'Failed to fetch portfolio stocks.';
        this.alertStore$.error(JSON.stringify(error.message || error));
      }
    }
  }
};
</script>

<style scoped>
.sector-analysis-page {
  padding: 16px;
}

.analysis-shell {
  display: grid;
  gap: 20px;
}

.analysis-panel {
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border: 1px solid #d9e6f2;
  border-radius: 20px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
  padding: 24px;
}

.sector-workspace {
  align-items: start;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(340px, 0.85fr) minmax(0, 1.35fr);
}

.sector-list-panel,
.deep-dive-panel {
  min-width: 0;
}

.sector-list-panel {
  max-height: calc(100vh - 32px);
  overflow-y: auto;
}

.deep-dive-panel {
  position: sticky;
  top: 16px;
}

.hero-panel {
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 20px;
  background:
    radial-gradient(circle at top right, rgba(14, 165, 233, 0.15), transparent 30%),
    linear-gradient(135deg, #eff6ff 0%, #ffffff 55%, #f8fafc 100%);
}

.eyebrow,
.section-kicker,
.suggestion-sector {
  margin: 0 0 6px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.72rem;
  color: #64748b;
  font-weight: 700;
}

.hero-copy h2,
.panel-header h3 {
  margin: 0;
  color: #0f172a;
}

.hero-description {
  margin: 12px 0 0;
  color: #475569;
  max-width: 70ch;
}

.hero-actions,
.hero-metrics,
.panel-header,
.sector-card-header,
.sector-values,
.weight-meta,
.deep-dive-summary,
.badge-stack,
.insight-row,
.stock-name,
.range-stack {
  display: flex;
}

.hero-actions {
  flex-direction: column;
  gap: 18px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #cbd5e1;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 14px;
  padding: 12px 14px;
}

.search-box input {
  border: 0;
  outline: none;
  background: transparent;
  width: 100%;
}

.export-button {
  align-items: center;
  align-self: flex-start;
  background: #0f172a;
  border: 0;
  border-radius: 12px;
  color: #fff;
  display: inline-flex;
  font-weight: 700;
  gap: 8px;
  justify-content: center;
  padding: 10px 14px;
}

.export-button:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.hero-metrics {
  gap: 12px;
  flex-wrap: wrap;
}

.hero-metrics > div,
.summary-pill {
  flex: 1;
  min-width: 130px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
}

.hero-metrics span,
.summary-pill span,
.sector-values span,
.weight-meta span {
  display: block;
  font-size: 0.8rem;
  color: #64748b;
}

.hero-metrics strong,
.summary-pill strong,
.sector-values strong,
.weight-meta strong {
  color: #0f172a;
}

.panel-header {
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.result-count,
.stock-count,
.overweight-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.result-count {
  background: #e0f2fe;
  color: #0369a1;
  padding: 8px 12px;
}

.sector-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.sector-card {
  text-align: left;
  border: 1px solid #dbe7f3;
  border-left-width: 6px;
  border-radius: 18px;
  background: #fff;
  padding: 18px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.sector-card:hover,
.sector-card.active {
  transform: translateY(-2px);
  box-shadow: 0 16px 30px rgba(15, 23, 42, 0.08);
}

.sector-card.active {
  border-color: #0ea5e9;
}

.border-success {
  border-left-color: #16a34a;
}

.border-danger {
  border-left-color: #dc2626;
}

.border-warning {
  border-left-color: #d97706;
}

.sector-card-header,
.sector-values,
.weight-meta,
.insight-row,
.stock-name,
.range-stack {
  justify-content: space-between;
  align-items: center;
}

.sector-card-header h4,
.suggestion-card h4 {
  margin: 0;
  color: #0f172a;
}

.sector-card-header p,
.insight-row p,
.suggestion-card p {
  margin: 4px 0 0;
  color: #64748b;
}

.stock-count {
  background: #f1f5f9;
  color: #334155;
  padding: 8px 10px;
}

.sector-values {
  margin: 18px 0 14px;
  gap: 12px;
}

.sector-values small {
  display: block;
}

.weight-row {
  display: grid;
  gap: 8px;
}

.weight-bar,
.range-bar {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
}

.weight-fill,
.range-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #0ea5e9, #2563eb);
}

.route-link {
  border: 0;
  background: #0f172a;
  color: #fff;
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 0.9rem;
}

.deep-dive-summary {
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.table-shell {
  max-height: calc(100vh - 270px);
  overflow-x: auto;
  overflow-y: auto;
}

.sector-stock-table {
  margin-bottom: 0;
  min-width: 980px;
}

.sector-stock-table th {
  color: #334155;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
}

.sector-stock-table td {
  vertical-align: middle;
  border-bottom: 1px solid #eef2f7;
}

.sortable {
  cursor: pointer;
}

.active {
  color: #0f172a;
}

.stock-name {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.stock-name small {
  color: #64748b;
}

.range-stack {
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}

.badge-stack {
  gap: 6px;
  flex-wrap: wrap;
}

.risk-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 0.74rem;
  font-weight: 700;
}

.risk-badge.success {
  background: #dcfce7;
  color: #166534;
}

.risk-badge.warning {
  background: #fef3c7;
  color: #92400e;
}

.risk-badge.danger {
  background: #fee2e2;
  color: #991b1b;
}

.risk-badge.neutral {
  background: #e2e8f0;
  color: #334155;
}

.exit-row td {
  background: #fff1f2;
}

.tab-strip {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.tab-button {
  border: 1px solid #dbe7f3;
  background: #fff;
  color: #475569;
  padding: 10px 14px;
  border-radius: 999px;
  font-weight: 700;
}

.tab-button.active {
  background: #0f172a;
  border-color: #0f172a;
  color: #fff;
}

.tab-panel {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 18px;
}

.suggestions-panel,
.suggestions-panel .empty-state,
.suggestions-panel .suggestion-grid {
  grid-column: 1 / -1;
}

.chart-card {
  min-height: 320px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px;
  position: relative;
}

.chart-card.wide {
  min-height: 420px;
}

.insight-list,
.suggestion-grid {
  display: grid;
  gap: 12px;
}

.insight-row,
.suggestion-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 16px;
}

.overweight-badge {
  background: #ffedd5;
  color: #9a3412;
  padding: 8px 10px;
}

.heatmap {
  display: grid;
  grid-template-columns: minmax(220px, 1.2fr) repeat(4, minmax(140px, 1fr));
  gap: 10px;
}

.heatmap-header,
.heatmap-sector,
.heatmap-cell {
  border-radius: 16px;
  padding: 14px;
}

.heatmap-header {
  background: #0f172a;
  color: #fff;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.heatmap-sector {
  background: #eff6ff;
  color: #1e3a8a;
  font-weight: 700;
}

.heatmap-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #e2e8f0;
}

.heat-low {
  background: #dcfce7;
  color: #166534;
}

.heat-medium {
  background: #fef3c7;
  color: #92400e;
}

.heat-high {
  background: #fee2e2;
  color: #991b1b;
}

.empty-state {
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  color: #64748b;
}

.error-state {
  border-color: #fecaca;
  color: #991b1b;
}

.text-success {
  color: #16a34a !important;
}

.text-danger {
  color: #dc2626 !important;
}

.text-muted {
  color: #64748b !important;
}

@media (max-width: 1200px) {
  .hero-panel,
  .tab-panel,
  .sector-workspace {
    grid-template-columns: 1fr;
  }

  .sector-list-panel {
    max-height: none;
    overflow-y: visible;
  }

  .sector-grid {
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  }

  .deep-dive-panel {
    position: static;
  }

  .table-shell {
    max-height: none;
    overflow-y: visible;
  }
}

@media (max-width: 767px) {
  .sector-analysis-page {
    padding: 10px;
  }

  .analysis-panel {
    padding: 18px;
    border-radius: 16px;
  }

  .heatmap {
    grid-template-columns: 1fr;
  }

  .heatmap-header:first-child {
    display: none;
  }
}
</style>
